<template>
  <component
    :is="element"
    class="product-prices"
    :class="[
      `product-prices--${template}`,
      {
        [$class('loading')]: loading,
        'is-percentage': activeDiscount && showSavingsNotification && price > 0,
        'is-discounted': activeDiscount,
      },
    ]"
  >
    <span
      v-if="hidePriceShowSaving && template == 'promoted'"
      v-text="$string('cart.summary.saved_label')"
    />

    <span
      class="product-prices__price"
      :class="{ 'product-prices__price--on-sale': formattedCompareAtPrice }"
    >
      <span
        v-if="varies"
        class="product-prices__varies"
        :class="textClasses.varies"
        v-text="$string('product.price.from')"
      />

      <span
        class="product-prices__sale-price"
        :class="textClasses.price"
        v-text="hidePriceShowSaving ? formattedPriceSaved : formattedPrice"
      />
      <!-- remove false if you want to show the tooltip again -->
      <div
        v-if="false && condDiscTooltipText && template === 'form'"
        class="product-prices__conditional-discount-tooltip-wrapper"
      >
        <svg
          class="product-prices__tooltip-arrow-tip product-prices__tooltip-arrow-tip--background"
          xmlns="http://www.w3.org/2000/svg"
          width="5"
          height="10"
          viewBox="0 0 5 10"
          fill="none"
        >
          <path d="M4.55951e-08 5L5 10L5 0L4.55951e-08 5Z" fill="#6E7173" />
        </svg>
        <svg
          class="product-prices__tooltip-arrow-tip product-prices__tooltip-arrow-tip--foreground"
          xmlns="http://www.w3.org/2000/svg"
          width="5"
          height="10"
          viewBox="0 0 5 10"
          fill="none"
        >
          <path d="M4.55951e-08 5L5 10L5 0L4.55951e-08 5Z" fill="#EEF1F2" />
        </svg>
        <div class="product-prices__conditional-discount-tooltip">
          <icon-info />
          <span v-text="condDiscTooltipText" />
        </div>
      </div>
    </span>

    <span
      v-if="formattedCompareAtPrice && !hidePriceShowSaving"
      class="product-prices__compare-at-price"
      :class="textClasses.compareAtPrice"
      v-text="formattedCompareAtPrice"
    />
    <div
      v-if="showSavingsNotification && activeDiscount && price > 0"
      class="product-prices__notification text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
    >
      <div class="product-prices__notification-inner">
        <span v-text="percentageSavedString" />
      </div>
    </div>
    <span
      v-if="
        (!hideUnitPriceWhenDiscounted || !formattedCompareAtPrice) &&
        unitPrice &&
        template !== 'predictive-search'
      "
      class="product-prices__unit-price"
      :class="textClasses.unitPrice"
      v-html="unitPriceString"
    />
    <span
      v-if="!unitPrice && template === 'form' && !isGiftCard"
      class="product-prices__unit-price"
      :class="textClasses.unitPrice"
      v-text="$string('product.price.no_unit_price')"
    />
  </component>
</template>

<script>
/**
 * Vue: Product prices (product-prices)
 * -----------------------------------------------------------------------------
 * Global product prices template.
 *
 * @param {Number|Boolean} compareAtPrice - Compare at price, in cents format.
 * @param {Object} [discountLogic] - Object for calculating discount.
 * @param {String} [element] - Custom element override.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number} price - Product price, in cents format.
 * @param {String} template - Template to use, 'form', 'card', 'cart', 'samples',
 * 'bought-together', 'predictive-search', 'promoted', 'promoted-modal'.
 * @param {String} variantId - Product Variant Id.
 * @param {Boolean|Number} [unitPrice] - Price per KG.
 * @param {Boolean} [usePromotedCampaign] - Use promoted campaign pricing.
 * @param {Boolean} [varies] - Product variants vary in price.
 * @param {Boolean} [isGiftCard] - Indicates if Product is Giftcard.
 * @param {Boolean} [showSavingNotification] - Displays tooltip with amount saved.
 * @param {Boolean} [hidePriceShowSaving] - Hide price and show saving instead in
 * campaign promotion box on PDP.
 * @param {Boolean} [hideUnitPriceWhenDiscounted] - Hides price/kg when price is
 * discounted (applied code, compare-at price set)
 *
 */
import { mapState } from 'pinia';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useCartStore from '~stores/cart';
import { isCampaignAppliedInCart } from '~/helpers/discounts';

import IconInfo from '~icons/general/info.svg';

export default {
  name: 'ProductPrices',

  components: {
    IconInfo,
  },

  props: {
    compareAtPrice: {
      type: [Boolean, Number],
      default: false,
      required: true,
    },
    basePrice: {
      type: [Boolean, Number],
      default: false,
    },
    element: {
      type: String,
      default: 'p',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    price: {
      type: [Boolean, Number],
      default: 0,
      required: true,
    },
    discountLogic: {
      type: Object,
      default: () => ({
        collectionIds: [],
        discountPreview: false,
        onSale: false,
        productId: 0,
        variantId: 0,
      }),
    },
    template: {
      type: String,
      default: 'product',
      required: true,
    },
    variantId: {
      type: [String, Number],
      default: '',
    },
    unitPrice: {
      type: [Boolean, Number],
      default: false,
    },
    unitPriceMeasurement: {
      type: [Boolean, Object, String],
      default: false,
    },
    unitNoBraces: {
      type: Boolean,
      default: false,
    },
    usePromotedCampaign: {
      type: Boolean,
      default: false,
    },
    varies: {
      type: Boolean,
      default: false,
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    showSavingsNotification: {
      type: Boolean,
      default: false,
    },
    hidePriceShowSaving: {
      type: Boolean,
      default: false,
    },
    hideUnitPriceWhenDiscounted: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      condDisc: false,
      localCurrentDiscount: false,
    };
  },

  computed: {
    /**
     * Map Pinia getters.
     */
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      getCurrentItemDiscountedPrice: 'getCurrentItemDiscountedPrice',
      getConditionalDiscount: 'getConditionalDiscount',
      activeCampaign: 'active',
    }),

    ...mapState(useCartStore, {
      cart: 'response',
      cartTotal: 'getIncludedCartTotal',
    }),

    /**
     * Compute current compare at price.
     * - If there's a discount and no compare at price then use original price.
     * - Otherwise use existing compare at price.
     * @returns {Number}
     */
    currentCompareAtPrice() {
      const localPrice = Number.parseInt(this.price.toFixed());
      if (
        (this.currentDiscount?.amount > 0 || this.currentPrice < localPrice) &&
        !this.compareAtPrice
      ) {
        return Number.parseInt(this.price.toFixed());
      }

      return this.compareAtPrice;
    },

    /**
     * Compute current price.
     * - Discounts price based on active or promoted campaigns that apply.
     * @returns {Number}
     */
    currentPrice() {
      if (this.price === false) {
        return false;
      }

      const localPrice = Number.parseInt(this.price.toFixed());

      let newPrice = localPrice;

      if (this.currentDiscount?.calculationType === 'perc') {
        const discountValue = (localPrice * this.discountMultiplier).toFixed();
        newPrice = Number.parseInt(localPrice - discountValue);
      }

      if (this.currentDiscount?.calculationType === 'fixed') {
        newPrice = this.currentDiscount.amount;
      }

      // sometimes comparePrice is a boolean
      // so we also need to check for this case as well
      if (newPrice < this.compareAtPrice || !this.compareAtPrice) {
        return newPrice;
      }
      return this.getCurrentItemDiscountedPrice(this.variantId, newPrice);
    },

    priceSaved() {
      return this.currentCompareAtPrice - this.currentPrice;
    },

    /**
     * Compute current unit price.
     * - Discounts price based on active or promoted campaigns that apply.
     * @returns {Number}
     */
    currentUnitPrice() {
      if (!this.unitPrice) {
        return 0;
      }

      if (!this.currentDiscount || this.currentDiscount?.amount === 0) {
        return this.unitPrice;
      }

      // on product-card this.price is the "from" price, not the price of the shown product
      // in this case we will have to use the basePrice of the local variant
      const productPrice = Number.parseInt(
        (this.basePrice || this.price).toFixed(),
      );

      let newPrice = productPrice;

      if (this.currentDiscount?.calculationType === 'perc') {
        const discountValue = (
          productPrice * this.discountMultiplier
        ).toFixed();
        newPrice = Number.parseInt(productPrice - discountValue);
      }

      if (this.currentDiscount?.calculationType === 'fixed') {
        newPrice = this.currentDiscount.amount;
      }

      // Scale Shopify's own (correctly unit-converted) unit price by the discount ratio,
      // instead of recalculating it from unitPriceMeasurement.quantityValue - that value's
      // unit (grams vs kilograms) isn't consistent across products, which previously caused
      // wildly wrong results for products whose quantityValue is already in kilograms.
      return Math.round(this.unitPrice * (newPrice / productPrice));
    },

    /**
     * Compute compare at price template.
     * @returns {String}
     */
    formattedCompareAtPrice() {
      return this.currentCompareAtPrice &&
        this.currentPrice < this.currentCompareAtPrice
        ? this.$formatMoney(this.currentCompareAtPrice)
        : '';
    },

    /**
     * Compute price template.
     * @returns {String}
     */
    formattedPrice() {
      if (this.currentPrice === false) {
        return this.$string('accessibility.loading');
      }

      if (this.currentPrice <= 0) {
        return this.$string('product.price.free');
      }

      return this.currentPrice ? this.$formatMoney(this.currentPrice) : '';
    },

    formattedPriceSaved() {
      return this.$formatMoney(this.priceSaved);
    },

    /**
     * Compute discount multiplier.
     * - Based on active or promoted campaign.
     * - Returns decimal to multiply price with to get the discounted price.
     * - 25% discount = 0.25.
     * @returns {Number}
     */
    discountMultiplier() {
      return this.currentDiscount?.amount
        ? this.currentDiscount.amount / 100
        : 0;
    },

    /**
     * Get currently applied/used discount value.
     * - Based on active or promoted campaign.
     * @returns {Number} I. e. 25 when discount is 25%
     */
    currentDiscount() {
      if (!this.discountLogic || this.isGiftCard) {
        return null;
      }

      if (this.usePromotedCampaign) {
        return this.getCampaignDiscount(this.discountLogic, 'promoted');
      }

      // The active campaign state can be stale (e.g. localStorage-persisted
      // from a previous code, repopulated asynchronously on hydration) -
      // only trust it once its code is confirmed against the cart, which is
      // the reliable source of truth for what's actually applied.
      if (!isCampaignAppliedInCart(this.activeCampaign, this.cart)) {
        return null;
      }

      return this.getCampaignDiscount(this.discountLogic, 'active');
    },

    /**
     * Computes price saved.
     * @returns {String}
     */
    priceSavedString() {
      return this.$string('product.price.saved', {
        replace: {
          amount: this.formattedPriceSaved,
        },
      });
    },

    /**
     * Computes percentage saved.
     * @returns {String}
     */
    percentageSavedString() {
      const percentage = Math.round(
        (this.priceSaved / this.currentCompareAtPrice) * 100,
      );
      return `-${percentage} %`;
    },

    /**
     * Compute text classes.
     * @returns {Object}
     */
    textClasses() {
      const textClasses = {
        compareAtPrice: 'title-mobile-m-bold',
        portions:
          'text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular',
        price: 'title-mobile-m-bold',
        unitPrice:
          'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular',
        varies:
          'text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular',
      };

      switch (this.template) {
        case 'bought-together':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular';
          textClasses.price =
            'text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          break;

        case 'card':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          textClasses.price =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-2xs-regular text-desktop-paragraph-3xs-regular';
          break;

        case 'card-compact':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold';
          textClasses.price =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-2xs-regular';
          textClasses.varies =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          break;

        case 'card-callout':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-2xl-regular text-desktop-paragraph-2xl-bold';
          textClasses.price =
            'text-mobile-paragraph-2xl-bold text-desktop-paragraph-2xl-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-2xs-regular text-desktop-paragraph-3xs-regular';
          break;

        case 'cart':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          textClasses.price =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          break;

        case 'predictive-search':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          textClasses.price =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold';
          break;

        case 'promoted':
          textClasses.compareAtPrice =
            'text-desktop-paragraph-l-regular display-mobile-headlines-h3';
          textClasses.price =
            'text-mobile-headlines-h4 text-desktop-headlines-h4';
          break;

        case 'samples':
          textClasses.compareAtPrice =
            'text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular';
          textClasses.price =
            'text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold';
          textClasses.unitPrice =
            'text-mobile-paragraph-2xs-regular text-desktop-paragraph-3xs-regular';
          break;
      }

      return textClasses;
    },

    /**
     * Computes unit price and returns string
     * @returns {String}
     */
    unitPriceString() {
      let unitMeasurement = this.unitPriceMeasurement?.referenceUnit
        ? this.unitPriceMeasurement?.referenceUnit
        : this.unitPriceMeasurement;

      unitMeasurement = unitMeasurement.toLowerCase();

      if (
        this.template === 'card' ||
        this.template === 'card-compact' ||
        this.template === 'samples' ||
        this.unitNoBraces
      ) {
        return this.$string('product.product_card.unit_price_html', {
          replace: {
            amount: this.$formatMoney(this.currentUnitPrice),
            unit: unitMeasurement,
            bracket_open:
              this.template === 'card-compact' || this.unitNoBraces ? '' : '(',
            bracket_close:
              this.template === 'card-compact' || this.unitNoBraces ? '' : ')',
          },
        });
      }

      if (this.template === 'cart') {
        return this.$string('product.price.unit_price_clean', {
          replace: {
            amount: this.$formatMoney(this.currentUnitPrice),
            unit: unitMeasurement,
          },
        });
      }

      if (this.$variable('product.unitPriceLink')) {
        return this.$string('product.price.unit_price_html', {
          replace: {
            amount: this.$formatMoney(this.currentUnitPrice),
            unit: unitMeasurement,
            classes:
              'text-mobile-paragraph-2xs-regular-link text-desktop-paragraph-3xs-regular-link',
            url: this.$variable('product.unitPriceLink'),
          },
        });
      }

      return this.$string('product.price.unit_price_no_link_html', {
        replace: {
          amount: this.$formatMoney(this.currentUnitPrice),
          unit: unitMeasurement,
        },
      });
    },

    activeDiscount() {
      return this.currentPrice < this.currentCompareAtPrice;
    },

    condDiscPercentage() {
      if (this.condDisc && this.localCurrentDiscount) {
        return Math.max(
          0,
          this.condDisc?.discPercentage - this.localCurrentDiscount.amount,
        );
      }

      return false;
    },

    condDiscAmount() {
      if (
        this.condDisc?.condType === 'minimumAmount' &&
        this.condDisc?.condValue
      ) {
        const currentMarket = cnvs.store.marketName;
        const matchingDiscount = this.condDisc.condValue.find(
          (discount) => discount.marketHandle === currentMarket,
        );
        return matchingDiscount?.amount || null;
      }

      return false;
    },

    condDiscTooltipText() {
      const percentage = this.condDiscPercentage;
      const amount = this.condDiscAmount;
      const currencySymbol = this.$variable('store.currencySymbol');
      if (
        percentage &&
        amount &&
        this.condDisc?.condType === 'minimumAmount' &&
        this.cartTotal < amount * 100
      ) {
        return this.$string(
          'discount_campaigns.conditional_discount.pdp_tooltip',
          {
            replace: { percentage, amount, currency_symbol: currencySymbol },
          },
        );
      }

      return false;
    },
  },

  watch: {
    cart() {
      this.updateConditionalDiscount();
    },

    variantId() {
      this.updateConditionalDiscount();
    },
  },

  methods: {
    setConditionalDiscount() {
      this.condDisc = this.getConditionalDiscount(
        [
          this.variantId,
          this.product?.id,
          ...(this.product?.collectionIds || []),
        ],
        'active',
      );
    },

    updateConditionalDiscount() {
      if (this.template === 'form') {
        this.localCurrentDiscount = this.currentDiscount;
        this.setConditionalDiscount();
      }
    },
  },
};
</script>
